import type * as React from "react";
import { type FC, type ReactNode, useCallback, useState } from "react";
import Modal from "~/common/components/Modal";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MarketingConsent } from "~/features/marketingConsent/components/MarketingConsent";
import { sendMarketingConsent } from "~/features/cms/api/giveMarketingConsent";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { Button, Stack } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";

interface Props {
  popupTitle?: string;
  consentIntro?: ReactNode;
  close: () => void;
  onOk?: () => void;
}

const ConsentPopup: FC<Props> = ({ close, popupTitle, consentIntro, onOk }) => {
  const { t } = useTranslation();

  const [newsletterOne, setNewsletterOne] = useState<boolean>(false);

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const [error, setError] = useState("");

  const doRegistration = useCallback(async () => {
    sendMarketingConsent(newsletterOne)
      .then(() => onOk && onOk())
      .catch((e) => setError(e));
  }, [onOk, newsletterOne]);

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // noinspection JSIgnoredPromiseFromCall
      doRegistration();
    },
    [doRegistration],
  );

  const footer = (
    <div className={"d-flex flex-justify-between flex-align-center w-100"}>
      <div className="scha__label--mandatory">{t("page.registerFree.mandatory")}</div>
      <Stack gap={1} direction={"row"}>
        <Button variant={"text"} data-test-id={"consent-cancel-button"} onClick={close}>
          {t("page.completeRegistration.cancel")}
        </Button>
        <Button
          data-test-id={"consent-signin-button"}
          disabled={!newsletterOne}
          onClick={doRegistration}
          fullWidth={!isDesktop}
        >
          {t("page.completeRegistration.submit")}
        </Button>
      </Stack>
    </div>
  );

  return (
    <Modal onClose={close} footerChildren={footer} hideClose={false} title={popupTitle}>
      <form onSubmit={onSubmit}>
        <button hidden type={"submit"} />

        {consentIntro && <div className="scha__consentintro">{consentIntro}</div>}

        <div>
          <MarketingConsent.ConsentCheckbox
            selected={newsletterOne}
            setSelected={setNewsletterOne}
            mandatory
            hideTitle
          />
        </div>

        {error && (
          <Alert className={"my-2"} severity={"error"}>
            {t(error)}
          </Alert>
        )}
      </form>
    </Modal>
  );
};
export default ConsentPopup;
